// Global "it's on you" indicator (§7 / UC-6): a small persistent pill, shown on
// every screen, counting the live games where it's your move plus the challenges
// waiting in your inbox — so a game that started while you're busy elsewhere,
// or a move an opponent is waiting on, isn't missed. Clicking jumps to the most
// time-sensitive item (a move you owe first, then a pending challenge).
//
// It refreshes on navigation, on session events (a challenge accepted / someone
// coming online), when the tab regains focus, and on a slow interval — there's
// no per-move server push for games you're not watching, so this stays a gentle
// poll rather than a live feed.

import { api } from "../api.ts";
import { navigate } from "../router.ts";
import { onSessionMessage } from "../presence.ts";
import { currentUser } from "../session.ts";
import { focusOrNavigate } from "./tabs.ts";
import { t } from "../i18n/index.ts";

let pill: HTMLButtonElement | undefined;
let target = "/";
let inFlight = false;
let lastAt = 0;

const REFRESH_MS = 25_000;
const THROTTLE_MS = 2_000;

/** Wire the alert pill once, at app start (after presence is available). */
export function initTurnAlerts(): void {
  onSessionMessage((m) => {
    if (m.t === "ready" || m.t === "online" || m.t === "presence") void refresh();
  });
  // Force on navigation so entering a game drops it from the count at once
  // (the `inFlight` guard still prevents overlap on rapid navigation).
  window.addEventListener("spa:navigated", () => void refresh(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void refresh(true);
  });
  window.setInterval(() => void refresh(true), REFRESH_MS);
  void refresh(true);
}

function el(): HTMLButtonElement {
  if (!pill) {
    pill = document.createElement("button");
    pill.type = "button";
    pill.id = "turn-alerts";
    pill.hidden = true;
    pill.setAttribute("aria-label", t("turnAlerts.label"));
    pill.onclick = () => {
      // A game target may already be open in another tab — focus it if so.
      const m = target.match(/^\/game\/([^/]+)/);
      if (m) void focusOrNavigate(m[1], () => navigate(target));
      else navigate(target);
    };
    document.body.append(pill);
  }
  return pill;
}

async function refresh(force = false): Promise<void> {
  if (inFlight) return;
  if (!force && performance.now() - lastAt < THROTTLE_MS) return;
  if (!currentUser()) {
    if (pill) pill.hidden = true;
    return;
  }
  if (document.visibilityState === "hidden") return;
  inFlight = true;
  try {
    paint(await compute());
    lastAt = performance.now();
  } catch {
    /* non-fatal: the pill just doesn't update this round */
  } finally {
    inFlight = false;
  }
}

interface Alerts {
  count: number;
  link: string;
  label: string;
}

async function compute(): Promise<Alerts> {
  // 1. Live games where it's my move — the most urgent (an opponent is waiting).
  let yourTurn = 0;
  let turnLink = "";
  try {
    const { games } = await api.listGames(20);
    const live = games.filter((g) => g.status === "started");
    const here = currentGameId();
    const snaps = await Promise.all(
      live.map((g) =>
        api
          .getGame(g.id)
          .then((f) => ({ g, f }))
          .catch(() => null),
      ),
    );
    for (const s of snaps) {
      if (!s || s.g.id === here) continue; // skip the game I'm already looking at
      // Re-check status: the game may have ended between the list and the snapshot.
      if (s.f.status === "started" && s.f.turn === s.g.color) {
        yourTurn++;
        if (!turnLink) turnLink = `/game/${s.g.id}`;
      }
    }
  } catch {
    /* ignore */
  }

  // 2. Challenges sitting in my inbox.
  let incoming = 0;
  let inboxLink = "";
  try {
    const { challenges } = await api.incomingChallenges();
    incoming = challenges.length;
    if (challenges[0]) inboxLink = `/i/${challenges[0].token}`;
  } catch {
    /* ignore */
  }

  return {
    count: yourTurn + incoming,
    link: turnLink || inboxLink || "/",
    label: yourTurn > 0 ? t("turnAlerts.yourMove") : t("turnAlerts.waiting"),
  };
}

function currentGameId(): string | null {
  const m = location.pathname.match(/^\/game\/([^/]+)/);
  return m ? m[1] : null;
}

function paint(a: Alerts): void {
  const p = el();
  if (a.count <= 0) {
    p.hidden = true;
    return;
  }
  target = a.link;
  p.replaceChildren();
  const badge = document.createElement("span");
  badge.className = "ta-count";
  badge.textContent = String(a.count);
  const label = document.createElement("span");
  label.className = "ta-label";
  label.textContent = a.label;
  p.append(badge, label);
  p.hidden = false;
}
